/**
 * Labstep
 *
 * @module containers/File/GetURL/Cached
 * @desc Cached version of FileGetURL to prevent re-fetching URLs on re-renders
 */

import { FileGetURLContainer } from 'labstep-web/containers/File/GetURL';
import type { File } from 'labstep-web/models/file.model';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { CachedFileURLProviderProps } from './types';

// Simple in-memory cache for file URLs to prevent re-fetching
const urlCache = new Map<string, string>();

// Custom hook to manage cached file URLs
const useCachedFileURL = (file: File): string | null => {
  const [url, setUrl] = useState<string | null>(() => {
    return urlCache.get(file.guid) || null;
  });
  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    // If we already have the URL cached, use it
    if (urlCache.has(file.guid)) {
      const cachedUrl = urlCache.get(file.guid)!;
      if (url !== cachedUrl) {
        setUrl(cachedUrl);
      }
      return;
    }

    // Only fetch if we don't have it cached and this is the first render
    if (isFirstRender) {
      setIsFirstRender(false);
    }
  }, [file.guid, url, isFirstRender]);

  return url;
};

// Component that only renders FileGetURLContainer if we don't have cached URL
export const CachedFileURLProvider: React.FC<
  CachedFileURLProviderProps
> = ({ file, children }) => {
  const cachedUrl = useCachedFileURL(file);

  if (cachedUrl) {
    return <>{children({ url: cachedUrl })}</>;
  }

  return (
    <FileGetURLContainer file={file}>
      {({ url }) => {
        // Cache the URL
        urlCache.set(file.guid, url);
        return <>{children({ url })}</>;
      }}
    </FileGetURLContainer>
  );
};
